import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck2, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { WinButton } from "@/v2/today3/WinCard";

const BADGE = { hit: ["Hit", "bg-[#EDF2EE] text-[#4F6B55]"], close: ["Close", "bg-[#FAF4E8] text-[#8A5A16]"], missed: ["Missed", "bg-[#F5EFEB] text-[#8C857B]"] };

function ReviewRow({ g, i, month }) {
  const [label, cls] = BADGE[g.suggestion.status];
  return (
    <li className="py-3 border-t border-[#EFECE6] first:border-t-0" data-testid={`review-goal-${i}`}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-[#2D2926] truncate">{g.title}</span>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${cls}`} data-testid={`review-status-${i}`}>{label} · {g.progress}/{g.target}</span>
      </div>
      <div className="h-1.5 mt-2 rounded-full bg-[#EDE7DD] overflow-hidden"><div className="h-full bg-[#C86D51]" style={{ width: `${g.pct}%` }} /></div>
      <p className="text-xs text-[#635E59] mt-2" data-testid={`review-suggestion-${i}`}>{g.suggestion.text}</p>
      {g.suggestion.status === "hit" && <div className="mt-2"><WinButton goal={g} month={month} testid={`review-share-${i}`} /></div>}
    </li>
  );
}

export function MonthReview({ r, setR, onEdit, onChange }) {
  const [busy, setBusy] = useState(false);
  if (!r?.show) return null;
  const dismiss = async () => { setR({ ...r, show: false }); try { await api.post("/v2/goals/review/dismiss"); } catch (e) { console.error("Month review dismiss failed:", e); } };
  const useThem = async () => {
    const goals = r.items.filter((g) => g.suggestion.action !== "swap")
      .map((g) => ({ id: g.current_id || undefined, title: g.title, pillar: g.pillar, action: g.action, minutes: g.minutes || 10, target: g.suggestion.target, project_key: g.project_key }));
    if (!goals.length) return onEdit();
    setBusy(true);
    try { await api.put("/v2/goals", { goals }); toast.success(`${r.next_label} is set.`); await dismiss(); onChange(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <section className="card p-5 rise" data-testid="month-review">
      <div className="flex items-start justify-between gap-3">
        <span className="eyebrow flex items-center gap-1.5"><CalendarCheck2 className="h-3.5 w-3.5" />{r.label} in review</span>
        <button onClick={dismiss} aria-label="Dismiss" data-testid="month-review-dismiss" className="h-9 w-9 -m-2 grid place-items-center text-[#8C857B]"><X className="h-4 w-4" /></button>
      </div>
      <p className="font-display text-xl mt-2 leading-snug">{r.items.filter((g) => g.suggestion.status === "hit").length} of {r.items.length} goals hit.</p>
      <ul className="mt-3">{r.items.map((g, i) => <ReviewRow key={g.id} g={g} i={i + 1} month={r.label} />)}</ul>
      {r.early && r.needs_setup ? (
        <div className="grid grid-cols-2 gap-2 mt-4">
          <button disabled={busy} onClick={useThem} data-testid="month-review-use" className="h-11 rounded-full bg-stone-900 text-white text-sm font-semibold disabled:opacity-50">Use these for {r.next_label}</button>
          <button onClick={onEdit} data-testid="month-review-edit" className="h-11 rounded-full border border-stone-300 text-sm font-semibold">Edit first</button>
        </div>
      ) : !r.early && <p className="text-xs text-[#8C857B] mt-3" data-testid="month-review-upcoming">On the 1st, CAMRYN will set up {r.next_label} from these.</p>}
    </section>
  );
}
